import { moduleActionContext } from "@/store/index";
import { AxiosError, AxiosResponse } from "axios";
import { defineModule } from "direct-vuex";
import VehicleRestModel from "../../models/rest/vehicle/vehicleRestModel";
import { axiosInstance } from "../../plugins/axios";
import router from "../../router";
import RegisteredVehicleTextRestModel from "../../models/rest/map/registeredVehicleTextRestModel";
import { DataOptions } from "vuetify";
import { YesNoAllEnum } from "@/models/emums/yesNoAllEnum";

export interface VehicleModuleStateInterface {
    vehicle: VehicleRestModel;
    yourVehicleList: VehicleRestModel[];
    yourVehicleListLoading: false | boolean;
    yourVehicleListTotalCount: number;
    yourVehiclesPagerOptions: DataOptions;
    yourVehiclesSearchText: string;
    vehicleMapList: VehicleRestModel[];
    vehicleMapListLoading: boolean;
    registeredVehicleMapList: VehicleRestModel[];
    registeredVehicleMapListLoading: boolean;
    showVehicleMapInfoDialog: boolean;
    vehicleMapInfoDialogMessage: string;
    vehicleMapInfoDialogColor: string;
    vehicleModifiedTime: string,
    showVehicleDialog: boolean;
    vehicleDialogTitle: string;
    vehicleDialogResolve: ((confirmed: boolean) => void) | null;
}

const vehicleModule = defineModule({
    namespaced: true,
    state: {
        vehicle: {} as VehicleRestModel,
        yourVehicleList: [] as VehicleRestModel[],
        yourVehicleListLoading: false,
        yourVehicleListTotalCount: 0,
        yourVehiclesPagerOptions: {
            page: 1,
            itemsPerPage: 10,
        } as DataOptions,
        yourVehiclesSearchText: "",
        vehicleMapList: [] as VehicleRestModel[],
        vehicleMapListLoading: false,
        registeredVehicleMapList: [] as VehicleRestModel[],
        registeredVehicleMapListLoading: false,
        showVehicleMapInfoDialog: false,
        vehicleMapInfoDialogMessage: "",
        vehicleMapInfoDialogColor: "",
        vehicleModifiedTime: "",
        showVehicleDialog: false,
        vehicleDialogTitle: "",
        vehicleDialogResolve: null,
    } as VehicleModuleStateInterface,
    mutations: {
        setVehicle(state, value: VehicleRestModel) {
            state.vehicle = value;
        },
        setYourVehicleList(state, value: VehicleRestModel[]) {
            state.yourVehicleList = value;
        },
        setYourVehicleListLoading(state, value: boolean) {
            state.yourVehicleListLoading = value;
        },
        setYourVehicleListTotalCount(state, value: number) {
            state.yourVehicleListTotalCount = value;
            if ((state.yourVehiclesPagerOptions.page * state.yourVehiclesPagerOptions.itemsPerPage) >= (value + state.yourVehiclesPagerOptions.itemsPerPage)) {
                state.yourVehiclesPagerOptions.page = 1;
            }
        },
        setYourVehiclesSearchText(state, value: string) {
            state.yourVehiclesSearchText = value;
        },
        setVehicleMapList(state, value: VehicleRestModel[]) {
            state.vehicleMapList = value;
        },
        setVehicleMapListLoading(state, value: boolean) {
            state.vehicleMapListLoading = value;
        },
        setRegisteredVehicleMapList(state, value: VehicleRestModel[]) {
            state.registeredVehicleMapList = value;
        },
        setRegisteredVehicleMapListLoading(state, value: boolean) {
            state.registeredVehicleMapListLoading = value;
        },
        setShowVehicleMapInfoDialog(state, value: boolean) {
            state.showVehicleMapInfoDialog = value;
        },
        setVehicleMapInfoDialogMessage(state, value: string) {
            state.vehicleMapInfoDialogMessage = value;
        },
        setVehicleMapInfoDialogColor(state, value: string) {
            state.vehicleMapInfoDialogColor = value;
        },
        setVehicleModifiedTime(state, value: string) {
            state.vehicleModifiedTime = value;
        },
        setShowVehicleDialog(state, value: boolean) {
            state.showVehicleDialog = value;
        },
        setVehicleDialogTitle(state, value: string) {
            state.vehicleDialogTitle = value;
        },
        setVehicleDialogResolve(state, value: ((confirmed: boolean) => void) | null) {
            state.vehicleDialogResolve = value;
        },
        setYourVehiclesPagerOptions(state, value: DataOptions) {
            state.yourVehiclesPagerOptions = value;
        }
    },
    actions: {
        yourVehicleListCleanFilters(context) {
            const { commit } = _moduleActionContext(context);
            const filterOptions = {
                page: 1,
                itemsPerPage: 10,
            } as DataOptions;
            commit.setYourVehiclesSearchText("");
            commit.setYourVehiclesPagerOptions(filterOptions);
        },
        loadYourVehicleList(
            context,
            payload: {
                page: number;
                itemsPerPage: number;
                searchText: string;
            }
        ) {
            const { commit } = _moduleActionContext(context);
            return new Promise((resolve, reject) => {
                commit.setYourVehicleListLoading(true);
                const params = {
                    page: payload.page,
                    pageSize: payload.itemsPerPage,
                    searchText: payload.searchText,
                };
                axiosInstance
                    .get("/api/vehicle/yourVehicleList", { params })
                    .then((resp: AxiosResponse) => {
                        commit.setYourVehicleList(resp.data.items);
                        commit.setYourVehicleListTotalCount(resp.data.totalItemsCount);
                        resolve(resp.data);
                    })
                    .catch((err: AxiosError) => reject(err))
                    .finally(() => {
                        commit.setYourVehicleListLoading(false);
                    });
            });
        },
        saveVehicle(context) {
            const { state } = _moduleActionContext(context);
            return new Promise((resolve, reject) => {
                axiosInstance
                    .post("/api/vehicle/save", state.vehicle)
                    .then((resp: AxiosResponse) => {
                        resolve(resp.data);
                    }).
                    catch((err: AxiosError) => reject(err));
            })
        },
        loadVehicle(context, payload: { id: number }) {
            const { commit } = _moduleActionContext(context)
            return new Promise((resolve, reject) => {
                axiosInstance
                    .get("/api/vehicle/" + payload.id)
                    .then((resp: AxiosResponse) => {
                        commit.setVehicle(resp.data);
                        resolve(resp.data);
                    })
                    .catch((err: AxiosError) => reject(err));
            })
        },
        loadRegisteredVehicle(context, payload: { id: number }) {
            const { commit } = _moduleActionContext(context)
            return new Promise((resolve, reject) => {
                axiosInstance
                    .get("/api/vehicle/registered-vehicle/" + payload.id)
                    .then((resp: AxiosResponse) => {
                        commit.setVehicle(resp.data);
                        resolve(resp.data);
                    })
                    .catch((err: AxiosError) => reject(err));
            })
        },
        deleteVehicle(context, payload: { id: number }) {
            const { state, dispatch } = _moduleActionContext(context)
            return new Promise((resolve, reject) => {
                axiosInstance
                    .post("/api/vehicle/delete/" + payload.id)
                    .then((resp: AxiosResponse) => {
                        dispatch.loadYourVehicleList({
                            searchText: state.yourVehiclesSearchText,
                            page: state.yourVehiclesPagerOptions.page,
                            itemsPerPage: state.yourVehiclesPagerOptions.itemsPerPage
                        });
                        resolve(resp.data);
                    })
                    .catch((err: AxiosError) => reject(err));
            })
        },
        removeNotify(context, payload: { id: number }) {
            return new Promise((resolve, reject) => {
                axiosInstance
                    .post("/api/vehicle/removeNotify/", { id: payload.id })
                    .then((resp: AxiosResponse) => {
                        resolve(resp.data);
                    })
                    .catch((err: AxiosError) => reject(err));
            })
        },
        removeRegisteredNotify(context, payload: { id: number }) {
            return new Promise((resolve, reject) => {
                axiosInstance
                    .post("/api/vehicle/removeRegisteredNotify/", { id: payload.id })
                    .then((resp: AxiosResponse) => {
                        resolve(resp.data);
                    })
                    .catch((err: AxiosError) => reject(err));
            })
        },
        reportNotify(context, payload: { id: number }) {
            return new Promise((resolve, reject) => {
                axiosInstance
                    .post("/api/vehicle/reportNotify/", { id: payload.id })
                    .then((resp: AxiosResponse) => {
                        resolve(resp.data);
                    })
                    .catch((err: AxiosError) => reject(err));
            })
        },
        changeStatus(context, payload: { id: number }) {
            const { state } = _moduleActionContext(context)
            return new Promise((resolve, reject) => {
                axiosInstance
                    .post("/api/vehicle/changeStatus/" + payload.id, state.vehicle)
                    .then((resp: AxiosResponse) => {
                        resolve(resp.data)
                        router.push({ path: "/vehicle/your-vehicles" });
                    })
                    .catch((err: AxiosError) => reject(err));
            });
        },
        async loadVehicleMapList(context, payload: { adr: YesNoAllEnum; tailLift: YesNoAllEnum, contractorSearchText: string | undefined }) {
            const { commit } = _moduleActionContext(context);
            return await new Promise((resolve, reject) => {
                commit.setVehicleMapListLoading(true);
                axiosInstance
                    .get("/api/vehicle/vehicleMapList", {
                        params:
                            { adr: payload.adr, tailLift: payload.tailLift, contractorSearchText: payload.contractorSearchText }
                    })
                    .then((resp: AxiosResponse) => {
                        commit.setVehicleMapList(resp.data);
                        resolve(resp.data);
                    })
                    .catch((err: AxiosError) => reject(err))
                    .finally(() => {
                        commit.setVehicleMapListLoading(false)
                    })
            })
        },
        async loadRegisteredVehicleMapList(context, payload: { contractorSearchText: string | undefined }) {
            const { commit } = _moduleActionContext(context);
            return await new Promise((resolve, reject) => {
                commit.setRegisteredVehicleMapListLoading(true);
                axiosInstance
                    .get("/api/vehicle/registeredVehicleMapList", {
                        params: { contractorSearchText: payload.contractorSearchText }
                    })
                    .then((resp: AxiosResponse) => {
                        commit.setRegisteredVehicleMapList(resp.data);
                        resolve(resp.data);
                    })
                    .catch((err: AxiosError) => reject(err))
                    .finally(() => {
                        commit.setRegisteredVehicleMapListLoading(false)
                    })
            })
        },
        holdOn(context, payload: { adr: YesNoAllEnum; tailLift: YesNoAllEnum, contractorSearchText: string }) {
            const { state, dispatch, rootDispatch } = _moduleActionContext(context)
            return new Promise((resolve, reject) => {
                axiosInstance
                    .post("/api/vehicle/holdOn/", state.vehicle)
                    .then((resp: AxiosResponse) => {
                        dispatch.loadVehicleMapList({ adr: payload.adr, tailLift: payload.tailLift, contractorSearchText: payload.contractorSearchText });
                        rootDispatch.logistic.loadLogisticLoadMapList();
                        resolve(resp.data)
                    })
                    .catch((err: AxiosError) => reject(err));
            })
        },
        holdOff(context, payload: { adr: YesNoAllEnum; tailLift: YesNoAllEnum, contractorSearchText: string }) {
            const { state, dispatch, rootDispatch } = _moduleActionContext(context)
            return new Promise((resolve, reject) => {
                axiosInstance
                    .post("/api/vehicle/holdOff/", state.vehicle)
                    .then((resp: AxiosResponse) => {
                        dispatch.loadVehicleMapList({ adr: payload.adr, tailLift: payload.tailLift, contractorSearchText: payload.contractorSearchText });
                        rootDispatch.logistic.loadLogisticLoadMapList();
                        resolve(resp.data)
                    })
                    .catch((err: AxiosError) => reject(err));
            })
        },
        showVehicleMapInfoDialog(context, payload: { showDialog: boolean; message: string; color: string; }) {
            const { commit } = _moduleActionContext(context)
            commit.setShowVehicleMapInfoDialog(payload.showDialog);
            commit.setVehicleMapInfoDialogMessage(payload.message);
            commit.setVehicleMapInfoDialogColor(payload.color);
        },
        async checkChanges(context) {
            const { state, commit } = _moduleActionContext(context)
            return await new Promise((resolve, reject) => {
                axiosInstance
                    .post("/api/vehicle/checkChanges")
                    .then((resp: AxiosResponse) => {
                        if (state.vehicleModifiedTime != resp.data) {
                            commit.setVehicleModifiedTime(resp.data)
                        }
                        resolve(resp.data)
                    })
                    .catch((err: AxiosError) => reject(err))
            })

        },
        isConfirmedVehicleDialog(context) {
            const { state, commit } = _moduleActionContext(context);
            if (typeof state.vehicleDialogResolve === "function") {
                state.vehicleDialogResolve(true);
            }
            commit.setVehicleDialogResolve(null);
            commit.setShowVehicleDialog(false);
        },

        isNotConfirmedVehicleDialog(context) {
            const { state, commit } = _moduleActionContext(context);
            if (typeof state.vehicleDialogResolve === "function") {
                state.vehicleDialogResolve(false);
            }
            commit.setVehicleDialogResolve(null);
            commit.setShowVehicleDialog(false);
        },
        openVehicleDialog(
            context,
            payload: {
                showDialog: boolean;
                title: string;
            }
        ) {
            const { commit } = _moduleActionContext(context);
            commit.setShowVehicleDialog(payload.showDialog);
            commit.setVehicleDialogTitle(payload.title);
            return new Promise(resolve => {
                commit.setVehicleDialogResolve(resolve);
            });
        },
        processRegisteredVehiclesText(context, payload: { registeredVehicleTextModel: RegisteredVehicleTextRestModel }) {
            return new Promise((resolve, reject) => {
                axiosInstance
                    .post("/api/vehicle/processRegisteredVehiclesText/", payload.registeredVehicleTextModel)
                    .then((resp: AxiosResponse) => {
                        resolve(resp.data);
                        return resp.data;
                    })
                    .catch((err: AxiosError) => reject(err));
            })
        },
    }
});

export default vehicleModule;
// eslint-disable-next-line
const _moduleActionContext = (context: any) => moduleActionContext(context, vehicleModule);
